/**
 * Helpers de apresentação do CHAT: chips de origem/status, avatar inicial,
 * hora relativa (estilo lista de conversas) e estrelas de avaliação.
 */
import { Smartphone, Star, UserRound } from 'lucide-react';
import type { ChatChannel, ChatOrigin, ChatStatus, DeliveryError } from '@erp/shared';
import {
  CHAT_ORIGIN_LABELS,
  DELIVERY_ERROR_LABELS,
  MAX_UPLOAD_BYTES,
  whatsappChannelLabel,
} from '@erp/shared';
import { Badge, type BadgeProps } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { errorMessage as genericErrorMessage } from '@/features/tickets/ticket-format';

/** Limite de upload formatado (ex.: "16 MB") — fonte única para composer e erros. */
export const MAX_UPLOAD_LABEL = `${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))} MB`;

/** Códigos de erro da API do chat com mensagem amigável em pt-BR (v8). */
const ERROR_LABELS: Record<string, string> = {
  file_too_large: `O arquivo excede o limite de ${MAX_UPLOAD_LABEL}.`,
  template_not_found: 'Modelo não encontrado — recarregue a lista e tente de novo.',
  // spec 025: a lista de modelos vem do manager (números da Meta) e do broker
  // (número da Blip); o 502 diz quem não respondeu
  whatsapp_manager_error:
    'Os modelos da Meta não carregaram: o manager do Zeepchat não respondeu. Tente de novo em instantes.',
  whatsapp_broker_error:
    'Os modelos da Blip não carregaram: o broker do Zeepchat não respondeu. Tente de novo em instantes.',
  whatsapp_templates_unavailable:
    'Os modelos não carregaram: nem o manager nem o broker do Zeepchat responderam. Tente de novo em instantes.',
  params_mismatch: 'Preencha todos os parâmetros do modelo antes de enviar.',
  chat_already_open: 'Já existe uma conversa aberta com este número.',
  // v8 Etapa 4: transferência para atendente OU departamento. permission_denied
  // (cancelar sem chat.cancel etc.) vem do fallback genérico do ticket-format.
  invalid_transfer: 'Escolha um atendente ou um departamento para transferir a conversa.',
  // spec 015: o destino entrou em pausa depois que a lista foi carregada
  attendant_paused: 'Esta pessoa está em pausa e não pode receber conversas agora.',
  // v11: 403 do POST /public/chat com o widget desligado em /settings/chat —
  // o visitante anônimo nunca deve ver o código cru.
  chat_disabled: 'O atendimento por chat está desativado no momento.',
  // spec 013: encerrar com motivo, retomar e motivos na configuração
  close_reason_required: 'Escolha o motivo do encerramento.',
  close_reason_not_found: 'Motivo não encontrado — recarregue a lista e tente de novo.',
  close_reason_inactive: 'Este motivo foi desativado — escolha outro.',
  close_reason_exists: 'Já existe um motivo com esse nome.',
  close_reason_in_use: 'Este motivo já foi usado em conversas — desative-o em vez de excluir.',
  already_archived: 'Esta conversa já foi encerrada.',
  not_in_inbox: 'Esta conversa já foi assumida por outra pessoa.',
  not_archived: 'Esta conversa não está encerrada.',
  // spec 028: "tentar de novo"
  already_resent: 'Esta mensagem já foi reenviada.',
  resend_unavailable: 'Não há mais o que reenviar desta mensagem — escreva de novo.',
  message_not_failed: 'Esta mensagem não falhou.',
  not_responsible: 'Assuma a conversa para responder ao solicitante.',
};

/**
 * Por que o apontamento pedido no encerramento não saiu (spec 013) — o
 * encerramento vale; a tela só explica o que faltou.
 */
const APPOINTMENT_ERROR_LABELS: Record<string, string> = {
  ticket_not_open: 'o ticket vinculado está fechado.',
  ticket_not_found: 'o ticket vinculado não foi encontrado.',
  invalid_period: 'o atendimento não tem duração (início e fim no mesmo instante).',
  permission_denied: 'você não tem permissão para lançar apontamentos.',
  appointments_unavailable: 'o serviço de apontamentos não está disponível.',
};

export function appointmentErrorMessage(code: string): string {
  return APPOINTMENT_ERROR_LABELS[code] ?? `o ticket recusou o apontamento (${code}).`;
}

export function errorMessage(e: unknown): string {
  if (e !== null && typeof e === 'object' && 'error' in e) {
    const code = (e as { error: unknown }).error;
    if (typeof code === 'string') {
      const label = ERROR_LABELS[code];
      if (label) return label;
    }
  }
  return genericErrorMessage(e);
}

/** Rótulos curtos para chips compactos da lista/filtro. */
export const ORIGIN_SHORT: Record<ChatOrigin, string> = {
  widget: 'Widget',
  whatsapp: 'WhatsApp',
  manual: 'Manual',
};

const ORIGIN_VARIANT: Record<ChatOrigin, BadgeProps['variant']> = {
  widget: 'info',
  whatsapp: 'success',
  manual: 'default',
};

export function OriginBadge({
  origin,
  short,
  className,
}: {
  origin: ChatOrigin;
  short?: boolean;
  className?: string;
}) {
  return (
    <Badge variant={ORIGIN_VARIANT[origin]} className={className} title={CHAT_ORIGIN_LABELS[origin]}>
      {short ? ORIGIN_SHORT[origin] : CHAT_ORIGIN_LABELS[origin]}
    </Badge>
  );
}

/**
 * Selo "Aguardando atendente" (spec 013): o chatbot desistiu — ou o bot
 * externo transferiu (spec 021) — e avisou o cliente que um humano vai
 * continuar. A conversa não pode ficar confundida com as outras da fila.
 */
export function WaitingAttendantBadge({ className }: { className?: string }) {
  return (
    <Badge variant="warning" className={cn('px-1.5 py-0 text-[10px]', className)} title="O bot passou a conversa para a equipe e avisou o cliente que um atendente vai continuar">
      Aguardando atendente
    </Badge>
  );
}

/**
 * Selo "com o bot" (spec 021): o número tem bot externo e ele ainda conduz a
 * conversa — ela aparece aqui, mas fica fora do "Precisa de resposta" até o
 * bot transferir. Assumir — ou responder, que assume antes — cala o bot: a API
 * pede isso à Zeepchat pela outbox (spec 021, Assumir).
 */
export function ExternalBotBadge({ className }: { className?: string }) {
  return (
    <Badge variant="info" className={cn('px-1.5 py-0 text-[10px]', className)} title="O bot do número ainda está conduzindo esta conversa — ela entra na fila quando o bot transferir. Assumir ou responder cala o bot.">
      com o bot
    </Badge>
  );
}

export function StatusBadge({
  status,
  canceled,
  className,
}: {
  status: ChatStatus;
  canceled: boolean;
  className?: string;
}) {
  if (status === 'archived') {
    return canceled ? (
      <Badge variant="destructive" className={className}>
        Cancelado
      </Badge>
    ) : (
      <Badge variant="success" className={className}>
        Encerrado
      </Badge>
    );
  }
  if (status === 'in_attendance') {
    return (
      <Badge variant="primary" className={className}>
        Em atendimento
      </Badge>
    );
  }
  return (
    <Badge variant="info" className={className}>
      Aguardando
    </Badge>
  );
}

export function initialOf(name: string | null | undefined): string {
  return (name?.trim().charAt(0) ?? '?').toUpperCase() || '?';
}

// o número e o nome como a pessoa os reconhece moram no @erp/shared: a conversa
// em PDF, gerada pela API, escreve os dois do mesmo jeito que a tela
export { chatDisplayName, formatWaPhone } from '@erp/shared';

/** Avatar circular com a inicial do solicitante — sem letra (o número), o ícone de pessoa. */
export function ChatAvatar({ name, className }: { name: string; className?: string }) {
  const hasLetter = /\p{L}/u.test(name.trim().charAt(0));
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground',
        className
      )}
    >
      {hasLetter ? initialOf(name) : <UserRound className="size-4" />}
    </span>
  );
}

/**
 * Por qual número da empresa a pessoa fala (spec 028, FR-003): "PoolTrack ·
 * Blip · +55 31 3527-5550" no cabeçalho; `short` na lista ("PoolTrack · Blip").
 */
export function ChannelChip({
  channel,
  short,
  className,
}: {
  channel: ChatChannel;
  short?: boolean;
  className?: string;
}) {
  const full = whatsappChannelLabel(channel);
  return (
    <span
      className={cn('inline-flex min-w-0 items-center gap-1 text-muted-foreground', className)}
      title={`Número da empresa: ${full}`}
    >
      <Smartphone className="size-3 shrink-0" aria-hidden />
      <span className="truncate">{short ? whatsappChannelLabel(channel, { short: true }) : full}</span>
    </span>
  );
}

/** o motivo curto de um envio que não chegou (spec 028, FR-006) */
export function deliveryErrorText(error: DeliveryError | null | undefined): string {
  return error ? DELIVERY_ERROR_LABELS[error.reason] : 'O provedor não confirmou a entrega';
}

const WEEKDAY = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/** Hoje → HH:MM; ontem → "Ontem"; até 7 dias → dia da semana; senão dd/mm. */
export function formatRelativeTime(value: string | Date): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (dayDiff <= 0) return formatHourMinute(d);
  if (dayDiff === 1) return 'Ontem';
  if (dayDiff < 7) return WEEKDAY[d.getDay()] ?? '';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function formatHourMinute(value: string | Date): string {
  return new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/** Estrelas 1–5: readonly (sem onRate) ou clicáveis (avaliação do widget). */
export function Stars({
  value,
  onRate,
  disabled,
  className,
}: {
  value: number | null;
  onRate?: (assessment: number) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex items-center gap-0.5', className)}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value !== null && n <= value;
        const star = (
          <Star
            aria-hidden
            className={cn(
              'size-4',
              filled ? 'fill-[#FAAD14] text-[#FAAD14]' : 'text-muted-foreground/40'
            )}
          />
        );
        if (!onRate) return <span key={n}>{star}</span>;
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onRate(n)}
            aria-label={`Avaliar com ${n} ${n === 1 ? 'estrela' : 'estrelas'}`}
            className="rounded-sm p-0.5 transition-transform outline-none hover:scale-110 focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50"
          >
            {star}
          </button>
        );
      })}
    </span>
  );
}
